"use client";

import { useState } from "react";
import { useUserProfile } from "@/state/globalStore";
import { usePrintSettingsActions } from "@/features/profile";
import type { PrintSettings } from "@/generated/fastapi";

// ---------------------------------------------
// Main Hook: Owns local UI state (optimistic state) and UI concerns.
// ---------------------------------------------
export default function usePrintSettingsUI() {
  const { userProfile } = useUserProfile();
  const { updatePrintSettingsSync, resetPrintSettingsSync } =
    usePrintSettingsActions();

  // Local state for print settings for immediate UI responsiveness and optimistic updates
  const [printSettings, setPrintSettings] = useState<PrintSettings>(
    userProfile.data.print_settings,
  );
  // Key to force reset of components if needed
  const [resetKey, setResetKey] = useState(0);

  // Handlers for updating and resetting print settings
  async function handleUpdate(updates: Partial<PrintSettings>) {
    setPrintSettings((prev) => ({ ...prev, ...updates }));
    const updatedData = await updatePrintSettingsSync(updates);
    if (updatedData) {
      setPrintSettings(updatedData);
    }
    return updatedData;
  }

  // Reset to default print settings
  async function handleReset() {
    const resetData = await resetPrintSettingsSync();
    if (resetData) {
      setPrintSettings(resetData);
      setResetKey((prev) => prev + 1);
    }
    return resetData;
  }

  // Handler for changing publish_for_days with validation
  function handlePublishForDaysChange(newValue: number) {
    if (!isNaN(newValue) && newValue >= 1 && newValue <= 14) {
      void handleUpdate({ publish_for_days: newValue });
    }
  }

  return {
    printSettings,
    setPrintSettings,
    resetKey,
    handleUpdate,
    handleReset,
    handlePublishForDaysChange,
  };
}
